import { useIsSearchV2 } from '@ab-core-functions/search';
import { BREAKPOINT } from '@ab-core/breakpoints';
import {
    ROUTES,
    getClassNames,
    resetFilter,
    useDisableBackgroundScroll,
    useIsMobile,
    useOnClickOutside
} from '@ab-core/functions';
import type { CategoryResult } from '@ab-core/graphql/dist';
import { useAuth } from '@ab-core/hooks';
import { useGetCategories, useGetProductPortfolioRestriction } from '@ab-core/hooks-v2';
import { Icon } from '@ab-core/icon';
import { navigationStore } from '@ab-core/store';
import Text, { FontTypes } from '@ab-core/text';
import { navigate } from 'gatsby';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { Backdrop } from './backdrop';
import { MenuWrapper, StyledCollectionItems, StyledContainer, Wrapper } from './collection.styled';
import { calculateCells } from './function';
import { CollectionV2 } from './indexV2';

const FIRST_MENU = 0;
const SECOND_MENU = 1;
const LAST_MENU = 2;

const Collection: FC = () => {
    const collectionItemsClassNamePrefix = 'collectionItems';
    const ref = useRef<HTMLDivElement>(null);
    const { user } = useAuth();

    const { productPortfolioRestriction, refetch } = useGetProductPortfolioRestriction(user?.contact?.id || '');

    const isSmartphone: boolean = useIsMobile(BREAKPOINT.SM);
    const [secondMenuOpen, setSecondMenuOpen] = useState(false);
    const [thirdMenuOpen, setThirdMenuOpen] = useState(false);
    const [firstItem, setFirstItem] = useState<CategoryResult>();
    const [secondItem, setSecondItem] = useState<CategoryResult>();
    const collectionOpen = navigationStore.getters.useCollectionNavigation();
    const isMobile: boolean = useIsMobile(BREAKPOINT.LG);
    const { categories } = useGetCategories();
    const items = categories?.children;

    const firstItemClicked = (item: CategoryResult) => {
        setFirstItem(item);
        setSecondMenuOpen(true);
        setThirdMenuOpen(false);
        setSecondItem(undefined);
    };

    useEffect(() => {
        setSecondMenuOpen(false);
        setThirdMenuOpen(false);
        setFirstItem(undefined);
        setSecondItem(undefined);
        refetch();
    }, [collectionOpen]);

    const secondItemClicked = (item: CategoryResult) => {
        let itemValue;

        if (isSmartphone) {
            itemValue = item;
        } else {
            const result = calculateCells({
                ...item,
                children: item?.children?.filter((child) => !hideRestriction?.includes(child?.id || ''))
            });
            const obj = { count: item.count, id: item.id, name: item.name, children: result };
            itemValue = obj;
        }

        setSecondItem(itemValue);
        setThirdMenuOpen(true);
    };

    const closeFirstMenu = () => navigationStore.setters.setCollectionNavigation(false);

    const closeSecondMenu = () => {
        setFirstItem(undefined);
        setSecondMenuOpen(false);
        setThirdMenuOpen(false);
    };
    const closeThirdMenu = () => {
        setSecondItem(undefined);
        setThirdMenuOpen(false);
    };
    const WAIT = 20;
    const search = (id: string) => {
        resetFilter(true);
        setTimeout(() => {
            navigate(`${ROUTES.SEARCH}?q=&f.categoryIds=${id}`);
        }, WAIT);
        navigationStore.setters.setCollectionNavigation(false);
    };

    const getLastMenu = (): number => {
        if (thirdMenuOpen) {
            return LAST_MENU;
        }

        if (secondMenuOpen) {
            return SECOND_MENU;
        }

        return FIRST_MENU;
    };

    const collectionButtonRefObject = navigationStore.getters.useCollectionButtonRef();

    if (collectionButtonRefObject) {
        useOnClickOutside([ref, collectionButtonRefObject], closeFirstMenu);
    }

    useDisableBackgroundScroll(!!collectionOpen);

    const hideRestriction = productPortfolioRestriction ? productPortfolioRestriction : [];
    const itemHasChildrenAnIsVisible = (item: CategoryResult): boolean => {
        return Boolean(
            item.children?.filter((child) => !hideRestriction?.includes(child?.id || '')).length &&
                !hideRestriction.includes(item?.id || '')
        );
    };

    return (
        <>
            {collectionOpen && items && (
                <Wrapper>
                    {!isMobile && <Backdrop />}
                    <StyledContainer className="content">
                        <MenuWrapper ref={ref} className="flex-direction-row">
                            <>
                                <StyledCollectionItems
                                    header={{
                                        title: 'Sortiment',
                                        hideBackButton: true,
                                        onClick: isMobile ? () => closeFirstMenu() : () => undefined
                                    }}
                                    className={getClassNames({
                                        prefix: collectionItemsClassNamePrefix,
                                        styleNames: ['1']
                                    })}
                                    lastMenu={getLastMenu()}
                                    items={items.filter(itemHasChildrenAnIsVisible)}
                                    activeItem={firstItem}
                                    itemOnClick={firstItemClicked}
                                    contentItem={(item) => (
                                        <>
                                            {!hideRestriction?.includes(item?.id || '') && (
                                                <>
                                                    {item.name}
                                                    <Icon name="ChevronRight" color="black" />
                                                </>
                                            )}
                                        </>
                                    )}
                                />
                                {secondMenuOpen && firstItem && (
                                    <StyledCollectionItems
                                        header={{
                                            title: firstItem.name || '',
                                            onClick: isMobile ? () => closeSecondMenu() : () => undefined
                                        }}
                                        className={getClassNames({
                                            prefix: collectionItemsClassNamePrefix,
                                            styleNames: ['2']
                                        })}
                                        lastMenu={getLastMenu()}
                                        items={firstItem?.children?.filter(itemHasChildrenAnIsVisible)}
                                        activeItem={secondItem}
                                        itemOnClick={secondItemClicked}
                                        contentItem={(item) => (
                                            <>
                                                {!hideRestriction?.includes(item?.id || '') && (
                                                    <>
                                                        {item.name}
                                                        <Icon name="ChevronRight" color="black" />
                                                    </>
                                                )}
                                            </>
                                        )}
                                    />
                                )}
                                {thirdMenuOpen && secondItem && (
                                    <StyledCollectionItems
                                        header={{
                                            title: secondItem.name || '',
                                            onClick: isMobile ? () => closeThirdMenu() : () => undefined
                                        }}
                                        className={getClassNames({
                                            prefix: collectionItemsClassNamePrefix,
                                            styleNames: ['3']
                                        })}
                                        colProps={{ sm: 4 }}
                                        lastMenu={getLastMenu()}
                                        items={secondItem?.children}
                                        activeItem={secondItem}
                                        itemOnClick={(item) => search(item.id || '')}
                                        hasBorder={false}
                                        contentItem={(item) => (
                                            <>
                                                {!hideRestriction?.includes(item?.id || '') &&
                                                    item.name &&
                                                    item.count && (
                                                        <Text
                                                            type={FontTypes.Subtitle}
                                                            content={`${item.name} (${item.count})`}
                                                        />
                                                    )}
                                            </>
                                        )}
                                    />
                                )}
                            </>
                        </MenuWrapper>
                    </StyledContainer>
                </Wrapper>
            )}
        </>
    );
};

const Switching: FC = () => {
    const { isSearchV2 } = useIsSearchV2();
    const isV2 = isSearchV2();

    if (isV2) {
        return <CollectionV2 />;
    }

    return <Collection />;
};

export default Switching;
